import React, { useEffect, useRef, useState } from "react";

export default function FounderAuthority() {
  return (
    <section className="relative z-10 px-4 py-10 md:py-14">
      <div className="max-w-7xl mx-auto">
        <p className="text-center text-[#C4FF3D] uppercase tracking-[0.28em] text-[11px] md:text-xs font-bold mb-3">
          Your Mentor
        </p>
        <h2 className="display-font text-center text-3xl md:text-5xl font-extrabold leading-tight mb-3">
          <span className="text-white">Exclusive Session with</span>{" "}
          <span className="lime-box">Nawaz Shaikh</span>
        </h2>
        <p className="text-center text-white/70 text-base md:text-lg mb-14">
          Founder of VIRALX Systems &middot; Marketing Scientist &bull; AI Business Growth Expert
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-10 lg:gap-14 items-start">
          {/* Portrait */}
          <div className="relative min-w-0">
            <div className="absolute -inset-6 bg-[radial-gradient(circle,rgba(196,255,61,0.18),transparent_60%)] blur-3xl" />
            <div className="relative lime-img-card mx-auto max-w-sm">
              <img
                src="https://customer-assets.emergentagent.com/job_viral-lp-preview/artifacts/fc0arvz5_IMG_4191-e1780923534628.jpg"
                alt="Nawaz Shaikh — Founder of ViralX"
                className="w-full h-auto"
                loading="lazy"
              />
            </div>
          </div>

          {/* Story */}
          <div>
            <div className="space-y-4 text-white/80 leading-relaxed text-[15px] md:text-base">
              <p>
                Nawaz is the founder of <span className="text-white font-semibold">ViralX</span> and one of India&apos;s most active voices at the intersection of AI, content and business growth. He works with experts, founders and brands to install content and client-acquisition systems that actually connect to revenue.
              </p>
              <p>
                His approach is deliberately un-glamorous: strip the hype away from AI and treat it as an operating layer for a real business &mdash; not a novelty feature. Positioning, content, capture, nurture, conversion. In that order.
              </p>
              <p className="text-white">
                Inside this workshop, he walks you through the <span className="text-[#C4FF3D] font-semibold">exact system he uses</span> to help experts turn expertise into a predictable premium-client engine.
              </p>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-3 md:gap-4">
              <Pill label="Founder" value="ViralX" />
              <Pill
                label="Followers"
                counter={{ target: 2, format: formatMillions, duration: 1800 }}
              />
              <Pill
                label="Experts Transformed"
                counter={{ target: 120000, format: formatIndianPlus, duration: 2200 }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Pill + animated counter ---------- */

function Pill({ label, value, counter }) {
  return (
    <div className="rounded-2xl border border-[rgba(196,255,61,0.25)] bg-[#0e1410] p-4 text-center">
      <div className="text-white/55 text-[10px] md:text-xs uppercase tracking-[0.2em] font-semibold mb-1">
        {label}
      </div>
      <div className="text-[#C4FF3D] text-sm md:text-base font-bold">
        {counter ? <AnimatedCounter {...counter} /> : value}
      </div>
    </div>
  );
}

function AnimatedCounter({ target, format, duration = 1800 }) {
  const [value, setValue] = useState(0);
  const nodeRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const el = nodeRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      // Fallback: jump to target immediately
      setValue(target);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            const startTime = performance.now();
            const tick = (now) => {
              const elapsed = now - startTime;
              const progress = Math.min(elapsed / duration, 1);
              // easeOutCubic
              const eased = 1 - Math.pow(1 - progress, 3);
              setValue(target * eased);
              if (progress < 1) requestAnimationFrame(tick);
              else setValue(target);
            };
            requestAnimationFrame(tick);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.35 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, duration]);

  return <span ref={nodeRef}>{format(value)}</span>;
}

/* ---------- Formatters ---------- */

// "2M" — animates 0.0M → 2M (drops trailing .0 at the end)
function formatMillions(v) {
  const rounded = Math.round(v * 10) / 10;
  const str = rounded % 1 === 0 ? `${rounded.toFixed(0)}` : `${rounded.toFixed(1)}`;
  return `${str}M`;
}

// "1,20,000+" — Indian number grouping with + suffix
function formatIndianPlus(v) {
  const n = Math.round(v);
  if (n < 1000) return `${n}+`;
  const str = String(n);
  const lastThree = str.slice(-3);
  const remaining = str.slice(0, -3);
  const withCommas = remaining.replace(/\B(?=(\d{2})+(?!\d))/g, ",");
  return `${withCommas},${lastThree}+`;
}
